import React, { useEffect, useState } from 'react';

const AboutSection = () => {
  const [orderCount, setOrderCount] = useState(0);
  const [shouldReduceMotion, setShouldReduceMotion] = useState(false);
  const targetOrders = 200;

  useEffect(() => {
    if (typeof window === 'undefined') {
      setShouldReduceMotion(true);
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

    const updateMotionPreference = () => {
      const mobileViewport = window.matchMedia('(max-width: 767px)').matches;
      setShouldReduceMotion(prefersReducedMotion?.matches || mobileViewport);
    };

    updateMotionPreference();

    prefersReducedMotion?.addEventListener?.('change', updateMotionPreference);
    window.addEventListener('resize', updateMotionPreference);

    return () => {
      prefersReducedMotion?.removeEventListener?.('change', updateMotionPreference);
      window.removeEventListener('resize', updateMotionPreference);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || shouldReduceMotion) {
      setOrderCount(targetOrders);
      return undefined;
    }

    let frameId;
    let startTime;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animateCount = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const runtime = timestamp - startTime;
      const progress = Math.min(runtime / 1800, 1);
      const eased = easeOutCubic(progress);
      setOrderCount(Math.floor(targetOrders * eased));

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animateCount);
      } else {
        setOrderCount(targetOrders);
      }
    };

    frameId = window.requestAnimationFrame(animateCount);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [targetOrders]);

  return (
  <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 scroll-mt-24">
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row items-center gap-12">
        <div className="md:w-1/2">
          <div className="bg-gray-200 rounded-xl aspect-square overflow-hidden shadow-lg relative">
            <img src="/images/about/baker.jpg" alt="Baker Nithuja" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 border-8 border-white opacity-20 pointer-events-none"></div>
          </div>
        </div>
        <div className="md:w-1/2">
          <span className="inline-block mb-3 text-pink-600 font-medium">ABOUT US</span>
          <h2 className="text-3xl sm:text-4xl font-bold font-serif text-gray-900 mb-6">Meet Baker Nithuja</h2>
          <div className="prose prose-lg text-gray-700 space-y-4">
            <p>
              With over 6 years of experience in the art of cake design, I combine culinary expertise with artistic
              vision to create edible masterpieces that delight both the eyes and palate.
            </p>
            <p>
              What began as a home baking passion has blossomed into a thriving business, serving hundreds of satisfied
              customers across Sri Lanka with cakes that become the centerpiece of their celebrations.
            </p>
            <p>
              Every cake is personally crafted by me using only the finest ingredients, ensuring the highest quality and
              attention to detail from initial consultation to final decoration.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-6">
            <div className="flex-1 min-w-[220px] rounded-2xl bg-white/85 p-6 shadow-lg ring-1 ring-slate-100">
              <div className="space-y-3">
                <div>
                  <p className="text-base font-semibold text-slate-900">200+ 5-star reviews</p>
                  <p className="text-sm text-slate-500">Google & Instagram love notes</p>
                </div>
                <div className="flex gap-1 text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="h-5 w-5 drop-shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3 rounded-xl border border-dashed border-pink-200 bg-pink-50/60 px-4 py-3 text-sm font-semibold text-pink-700">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                </svg>
                Serving Sri Lanka since 2019
                
              </div>
            </div>

            <div className="relative flex-1 min-w-[240px] overflow-hidden rounded-2xl p-6 text-white order-glow-card shadow-xl">
              <span className="order-pulse-ring" aria-hidden="true" />
              <span className="order-spark" aria-hidden="true" />
              <span className="order-spark order-spark-delay" aria-hidden="true" />
              <p className="text-xs uppercase tracking-[0.4em] text-white/80">Orders Crafted</p>
              <div className="mt-2 text-4xl font-bold font-serif" aria-live="polite">
                {orderCount.toLocaleString()}+
              </div>
              <p className="mt-1 text-white/85 text-sm">Custom celebrations delivered around Kilinochchi.</p>
              <div className="mt-4 inline-flex items-center text-sm font-semibold">
                <span className="mr-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 backdrop-blur">
                  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M4 17h16l-2-7H6l-2 7z" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M7 10c0-2.761 2.239-5 5-5s5 2.239 5 5" strokeLinecap="round" />
                    <circle cx="9" cy="19" r="1.5" />
                    <circle cx="15" cy="19" r="1.5" />
                  </svg>
                </span>
                Fresh designs every week
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

export default AboutSection;
